// External component imports
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Grid } from '@interstate/components/Grid';

// Constants and types
import type { IVerificationTrustMap } from './IdVerification.mappings';
import { trustReasonMap } from './IdVerification.mappings';
import {
    COMPLIANCE,
    COMPLIANCE_ID_VERIFICATION_MESSAGE,
    DOCUMENT_VERIFICATION_TRUST_KEY,
    DOCUMENT_VERIFICATION_TRUST_REASONS_KEY
} from '../../shared/constants';

// Local style imports
import { ContainerBox, useStyles } from '../ComplianceAccordian.style';
import { Separator } from '../../ComplianceForm.style';

// Local utility imports
import { defineDocTrustReasons, getStatus } from '../../shared/utils';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import { useSelector } from 'react-redux';
import type { BinaryValues, ComplianceSections, StatusValue } from '../../shared/types';
import { ComplianceStatus } from '../../shared/types';
import { StatusIcon } from '../../shared/StatusIcon';
import { Alert } from '@interstate/components/Alert';

export const IdVerificationDetails = () => {
    const theme = useInterstateTheme();
    const classes = useStyles();
    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);

    const data = (participantMatch?.compliance ?? {}) as Partial<ComplianceSections>;
    const IdVerificationData = data.idVerification ?? {};

    const findings = Array.isArray(IdVerificationData.findings) ? IdVerificationData.findings : [];
    const documentVerificationTrustValue = findings.find((item: any) => item.name === DOCUMENT_VERIFICATION_TRUST_KEY)?.value || '';

    const status = getStatus(IdVerificationData.status, documentVerificationTrustValue as BinaryValues);

    const isPassOrFailStatus = status === ComplianceStatus.PASS || status === ComplianceStatus.FAIL;

    const message =
        COMPLIANCE_ID_VERIFICATION_MESSAGE.MESSAGES[status as keyof typeof COMPLIANCE_ID_VERIFICATION_MESSAGE.MESSAGES] || '';

    const documentVerificationTrustReasonsValue =
        findings.find((item: any) => item.name === DOCUMENT_VERIFICATION_TRUST_REASONS_KEY)?.value || '';

    const docTrustReasons =
        findings &&
        defineDocTrustReasons(documentVerificationTrustReasonsValue)
            .map((reason) => trustReasonMap[reason])
            .filter((mapped): mapped is IVerificationTrustMap => Boolean(mapped));

    // Handle ERROR
    if (status === ComplianceStatus.ERROR) {
        return (
            <>
                <Alert data-testid="error-alert" title={COMPLIANCE.ERROR_ALERT.TITLE} type="error">
                    <span>{COMPLIANCE.ERROR_ALERT.DESCRIPTION}</span>
                </Alert>
                <Separator
                    isVisible={true}
                    theme={theme}
                    className="card_separator"
                    data-testid="card_separator"
                    aria-hidden="true"
                />
            </>
        );
    }

    return (
        <ContainerBox>
            <Typography variant="body-sm">{message.details}</Typography>

            {docTrustReasons.length > 0 && isPassOrFailStatus && (
                <>
                    <Separator
                        isVisible={true}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_separator"
                        aria-hidden="true"
                    ></Separator>
                    <Typography variant="strong-sm">{COMPLIANCE.SECTION_TITLES.DETAILS}</Typography>
                    <Separator
                        isVisible={false}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_hidden_separator"
                        aria-hidden="true"
                    ></Separator>
                    {docTrustReasons.map((reason: any) => (
                        <Grid
                            key={`doc-trust-reason-${reason.name}`}
                            className={classes.container}
                            data-testid={`doc-trust-reason-${reason.name}`}
                        >
                            <Grid xs="auto" className={classes.iconWrapper}>
                                <StatusIcon
                                    status={reason.status as StatusValue.Passed | StatusValue.Failed | StatusValue.Undetermined}
                                />
                            </Grid>
                            <Grid xs="auto" className={classes.textWrapper}>
                                <Typography variant="strong-sm">{reason.name}</Typography>
                                <Typography variant="body-sm">{reason.description}</Typography>
                            </Grid>
                        </Grid>
                    ))}
                </>
            )}
        </ContainerBox>
    );
};
